/**
 *  このファイルはtools/generate.tsにより生成されたファイルです。手動で編集しないでください。
 */

import type { IDB } from "../indexedDB";
import type { EntityDataApi } from "../IndexedDBApi";
import type { ExtendedDB } from "../../../types/generated/extendedDBCore";

// ExtendedDB コレクションキー → 対応する IDB DataApi アクセサ。
// 各キーの DataApi はそのコレクションの行型(ExtendedDB[K][number])で厳密に型付けされる。
// 注意(命名衝突): ExtendedDB.materials = 材料マスタ(IDB MaterialMasters),
//                 ExtendedDB.materialQrs = QR個体(IDB Materials)。
export const keyToDataApi: { [K in keyof ExtendedDB]: (idb: IDB) => EntityDataApi<ExtendedDB[K][number]> } = {
  materialQrs: (idb) => idb.materialDataApi,
  issuances: (idb) => idb.issuanceDataApi,
  carryOuts: (idb) => idb.carryOutDataApi,
  lots: (idb) => idb.lotDataApi,
  carryIns: (idb) => idb.carryInDataApi,
  constructionPlanAreas: (idb) => idb.constructionPlanAreaDataApi,
  constructions: (idb) => idb.constructionDataApi,
  areaDivisions: (idb) => idb.generated.areaDivision,
  materialDivisions: (idb) => idb.generated.materialDivision,
  clients: (idb) => idb.generated.client,
  works: (idb) => idb.generated.work,
  workTypes: (idb) => idb.generated.workType,
  constructionMethods: (idb) => idb.generated.constructionMethod,
  stirringMethods: (idb) => idb.generated.stirringMethod,
  materials: (idb) => idb.generated.materialMaster,
  carryInLocations: (idb) => idb.generated.carryInLocation,
  locationDivisions: (idb) => idb.generated.locationDivision,
  workSubtypes: (idb) => idb.generated.workSubtype,
  materialDetails: (idb) => idb.generated.materialDetail,
  potLifeSpecs: (idb) => idb.generated.potLifeSpec,
  quantityPlans: (idb) => idb.generated.quantityPlan,
  stockOrders: (idb) => idb.generated.stockOrder,
  constructionPlanAreaLocations: (idb) => idb.generated.constructionPlanAreaLocation,
  constructionPlanAreaPlannedMaterials: (idb) => idb.generated.constructionPlanAreaPlannedMaterial,
  mixRatioDefinitions: (idb) => idb.generated.mixRatioDefinition,
  workDetails: (idb) => idb.generated.workDetail,
  mixRatioDetails: (idb) => idb.generated.mixRatioDetail,
  qualityMeasurementItems: (idb) => idb.generated.qualityMeasurementItem,
  processExecutions: (idb) => idb.generated.processExecution,
  stirBatches: (idb) => idb.generated.stirBatch,
  constructionActions: (idb) => idb.generated.constructionAction,
  qualityRecords: (idb) => idb.generated.qualityRecord,
  constructionActionAreas: (idb) => idb.generated.constructionActionArea,
  constructionMaterialConsumptions: (idb) => idb.generated.constructionMaterialConsumption,
  workTechnicians: (idb) => idb.generated.workTechnician,
  areaDivisionLocationLabels: (idb) => idb.generated.areaDivisionLocationLabel,
  locationDivisionParentPaths: (idb) => idb.generated.locationDivisionParentPath,
};

// ExtendedDB コレクションキー → schema.json 上のエンティティ名。
// relationEdges.ts のようにエンティティ名で書かれた生成物と、コレクションキーで書かれた
// 実装コードを突き合わせるための対応表。実行時に IndexedDB インスタンスを必要としないため、
// 純関数からも参照できる。
export const collectionKeyToEntityName = {
  materialQrs: "Material",
  issuances: "Issuance",
  carryOuts: "CarryOut",
  lots: "Lot",
  carryIns: "CarryIn",
  constructionPlanAreas: "ConstructionPlanArea",
  constructions: "Construction",
  areaDivisions: "AreaDivision",
  materialDivisions: "MaterialDivision",
  clients: "Client",
  works: "Work",
  workTypes: "WorkType",
  constructionMethods: "ConstructionMethod",
  stirringMethods: "StirringMethod",
  materials: "MaterialMaster",
  carryInLocations: "CarryInLocation",
  locationDivisions: "LocationDivision",
  workSubtypes: "WorkSubtype",
  materialDetails: "MaterialDetail",
  potLifeSpecs: "PotLifeSpec",
  quantityPlans: "QuantityPlan",
  stockOrders: "StockOrder",
  constructionPlanAreaLocations: "ConstructionPlanAreaLocation",
  constructionPlanAreaPlannedMaterials: "ConstructionPlanAreaPlannedMaterial",
  mixRatioDefinitions: "MixRatioDefinition",
  workDetails: "WorkDetail",
  mixRatioDetails: "MixRatioDetail",
  qualityMeasurementItems: "QualityMeasurementItem",
  processExecutions: "ProcessExecution",
  stirBatches: "StirBatch",
  constructionActions: "ConstructionAction",
  qualityRecords: "QualityRecord",
  constructionActionAreas: "ConstructionActionArea",
  constructionMaterialConsumptions: "ConstructionMaterialConsumption",
  workTechnicians: "WorkTechnician",
  areaDivisionLocationLabels: "AreaDivisionLocationLabel",
  locationDivisionParentPaths: "LocationDivisionParentPath",
} as const;